import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { pluralReadableTypeName, readableTypeName } from '@iotv/core-services';
import { AdjacentType, Device, DeviceClassName, getItemTypeToGroupNestedDescendingQueryDef, getParentEdgeTypeId } from '@iotv/datamodel-core';
import { AppValueTypes, ComparisonType, LogicalOperatorType } from '@iotv/iotv-v3-types';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Stack, Tab } from '@mui/material';
import React, { useState } from 'react';
import { isAdminRole } from '../../helpers/access';
import { useMostProximateCustomer } from '../../hooks/useMostProximateCustomer';
import { useTabHandlerStringKeys } from '../../hooks/useTabHandler';
import useQuestion from '../../Popup/useQuestion';
import { ComponentMode, ObjectViewType, ShowDefinedEntriesOnlyOpts, } from '../../types';
import { getViewDefinitionForNewObject } from '../../v2/components/ExceptionTypesHandling/viewDefinitions';
import { standardDeleteItem } from '../../v2/components/generic/ObjectTree/standardTransactionVariantFns';
import { ObjectDetailCard } from '../../v2/components/generic/ObjectView/ObjectDetailCard';
import { ObjectHeader } from '../../v2/components/generic/ObjectView/ObjectHeader';
import { commonListViewDefinition, getListViewDefinition, getViewDefintionFromObject } from '../factories/AttributeDefinitions';
import { ListerlizerAdapter } from '../factories/Listerlizer/legacy/ListerlizerAdapter';
import { groupFilter, groupIsAllowedByUserGroup } from '../SytemManagement/Devices/DeviceGatewayGroupViewSubParts';
const debug = process.env.REACT_APP_DEBUG && false;
export const DeviceGroupView = ({ viewObject, contextCustomer, user, userSelectedGroup, applicationData, userFunctions, transactionFunctions, allowedActions, }) => {
    const contextObject = viewObject.matchedPrimary;
    const supportedTypeName = readableTypeName(contextObject?.supportedType);
    const pluralSupportedTypeName = pluralReadableTypeName(supportedTypeName);
    const [resolve, setResolve] = useState(() => () => { });
    const [confirmation, setConfirmation] = useState({ title: 'Confirm?', question: 'Are you sure?' });
    const { Dialogue, setOpen } = useQuestion({ ...confirmation, result: (r) => resolve(r) });
    const getConfirmation = (props) => {
        setConfirmation(props);
        setOpen(true);
        return new Promise((resolve) => setResolve(() => resolve));
    };
    const mostProximateCustomer = useMostProximateCustomer({ item: contextObject });
    /** INFO: Should be used for Listerlizer, but not passed to ObjectDetailCard! */
    const mutatedTransactionFunctions = {
        ...transactionFunctions,
        deleteObject: async (vob) => {
            await standardDeleteItem({ item: vob, contextUser: user, mostProximateCustomer, UserFunctions: userFunctions, getConfirmation });
        },
    };
    const adjacentFilter = {
        adjacentType: AdjacentType.CHILD,
        objectType: contextObject?.supportedType,
        edgeFilter: {
            edgeTypeId: getParentEdgeTypeId(contextObject?.supportedType, 'Group') ?? `never returned for ${supportedTypeName} (DeviceGroupView)`,
        },
    };
    const defaultViewDefinition = {
        name: {
            key: 'name',
            label: 'Name',
            type: AppValueTypes.STRING,
            editable: false,
        },
    };
    const deviceViewDefinition = getListViewDefinition(DeviceClassName) ?? commonListViewDefinition;
    const newDeviceViewDefinition = getViewDefinitionForNewObject(new Device({}));
    const groupViewDefinition = contextObject ? getViewDefintionFromObject(contextObject) : defaultViewDefinition;
    const maxCols = 10;
    const viewDefinitionColumnVisibility = Object.fromEntries(Object.keys(deviceViewDefinition ?? {})
        .slice(maxCols)
        .map((k) => [k, false]));
    const columnVisibilityReferences = {
        dataTableReference: 'ListCardV1',
        objectTypeId: adjacentFilter.objectType,
        viewType: ObjectViewType.List,
        externalColumnVisibility: viewDefinitionColumnVisibility,
    };
    const tabs = {
        [contextObject?.supportedType]: _jsx(Tab, { label: pluralSupportedTypeName, value: contextObject?.supportedType }),
        Groups: _jsx(Tab, { label: "Sub Groups", value: "Groups" }),
    };
    if (isAdminRole(userSelectedGroup)) {
        tabs.Visibility = _jsx(Tab, { label: "Visible to User Group", value: "Visibility" });
    }
    const { state: [[value, setValue]], } = useTabHandlerStringKeys(tabs);
    return (_jsxs(_Fragment, { children: [_jsx(ObjectHeader, { vob: contextObject }), _jsxs(Stack, { gap: 1, children: [contextObject && (_jsx(ObjectDetailCard, { matchedPrimary: contextObject, viewDefinition: groupViewDefinition, definedEntriesOnlyOpt: ShowDefinedEntriesOnlyOpts.Yes, allowedActions: allowedActions, mostProximateCustomer: mostProximateCustomer, applicationData: applicationData, userFunctions: userFunctions, transactionFunctions: transactionFunctions })), _jsxs(TabContext, { value: value, children: [_jsx(TabList, { onChange: (e, newValue) => setValue(newValue), children: Object.values(tabs) }), _jsx(TabPanel, { value: contextObject?.supportedType, sx: { padding: 0 }, children: _jsx(ListerlizerAdapter, { initMode: ComponentMode.EDIT, initialSort: [{ field: 'name', sort: 'asc' }], viewDefinition: deviceViewDefinition, columnVisibilityReferences: columnVisibilityReferences, viewObject: viewObject, searchRootObject: undefined, contextObject: contextObject, adjacentFilter: adjacentFilter, mostProximateCustomer: mostProximateCustomer, newObjectViewDefinition: newDeviceViewDefinition, userFunctions: userFunctions, transactionFunctions: mutatedTransactionFunctions, allowTransactionFunctionMutations: true }) }), _jsx(TabPanel, { value: "Groups", sx: { padding: 0 }, children: _jsx(ListerlizerAdapter, { initMode: ComponentMode.EDIT, initialSort: [{ field: 'name', sort: 'asc' }], viewDefinition: groupViewDefinition, columnVisibilityReferences: columnVisibilityReferences, viewObject: viewObject, searchRootObject: undefined, contextObject: contextObject, adjacentFilter: groupFilter, userFunctions: userFunctions, transactionFunctions: mutatedTransactionFunctions }) }), _jsx(TabPanel, { value: "Visibility", sx: { padding: 0 }, children: _jsx(ListerlizerAdapter, { viewObject,
                                    contextObject,
                                    adjacentFilter: groupIsAllowedByUserGroup,
                                    branchingQueryDefinition: getItemTypeToGroupNestedDescendingQueryDef({
                                        groupDefiningObjectTypeId: 'Customer',
                                        supportedType: 'User',
                                        includeSupportedTypeInstances: false,
                                    }),
                                    resultFilter: {
                                        objectTypeId: 'Group',
                                        nodeFilters: [
                                            {
                                                setOperation: LogicalOperatorType.AND,
                                                filters: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: 'User' }],
                                            },
                                        ],
                                    },
                                    initialSort: [{ field: 'name', sort: 'asc' }],
                                    initMode: ComponentMode.EDIT,
                                    userFunctions,
                                    viewDefinition: groupViewDefinition,
                                    transactionFunctions,
                                    searchRootObject: contextCustomer,
                                    columnVisibilityReferences }) })] })] }), Dialogue] }));
};
